import { Controller } from 'stimulus';

/* Provides an media player that is fixed to the bottom of the screen. */
export default class MediaPlayer extends Controller {
  connect() {
    this.mediaElement = document.getElementById(this.element.dataset.mediaId);
    this.episodeId = this.mediaElement.dataset.episodeId;

    this.mediaElement.addEventListener('play', this.playStarted.bind(this));
    this.mediaElement.addEventListener('pause', this.paused.bind(this));
    this.mediaElement.addEventListener('timeupdate', this.timeUpdate.bind(this));
    this.mediaElement.addEventListener("loadedmetadata", this.setup.bind(this));

    this.setPlaybackRateVisibility();
  }

  setup() {
    this.loadingButtonTargets.forEach(el => el.classList.add('hidden'));
    this.playButtonTargets.forEach(el => el.classList.remove('hidden'));

    this.scrubberTarget.addEventListener('pointerdown', this.startScrub.bind(this));
    this.scrubberTarget.addEventListener('pointerup', this.endScrub.bind(this));
    this.scrubberTarget.addEventListener('pointermove', this.scrub.bind(this));
    this.mediaElement.addEventListener('click', this.togglePlay.bind(this));

    const time = this.timeFromDataset() || this.timeFromLog() || 0;
    this.time = time;
    this.mediaElement.currentTime = time;
    this.setScrubPosition();
    this.play();
  }

  timeFromLog() {
    const episodeId = this.episodeId;
    const { time } = JSON.parse(localStorage.getItem(`media-player-episode-${episodeId}`) || "{}");
    return time;
  }

  timeFromDataset() {
    if (this.element.dataset.timestamp) {
      return parseInt(this.element.dataset.timestamp);
    } else {
      return null;
    }
  }

  startScrub(event) {
    this.isScrubbing = true;
  }

  // Advance the scrubber position to the mouse position without actually changing the media
  scrub(event) {
    if (!this.isScrubbing) {
      return false;
    }

    const scrubberRect = this.scrubberTarget.getBoundingClientRect();
    const percent = (event.clientX - scrubberRect.left) / scrubberRect.width;
    this.scrubberHandleTarget.style.left = `${percent * 100}%`;
    this.scrubberDoneTarget.style.width = `${percent * 100}%`;
    this.scrubberRangeTarget.value = Math.trunc(this.mediaElement.duration * percent);
    this.currentTimeTarget.innerText = this.formatTime(this.mediaElement.duration * percent);
  }

  // Advance the media to the scrubber position
  endScrub(event) {
    this.isScrubbing = false;
    const scrubberRect = this.scrubberTarget.getBoundingClientRect();
    const percent = (event.clientX - scrubberRect.left) / scrubberRect.width;
    this.mediaElement.currentTime = this.mediaElement.duration * percent;
  }

  togglePlay() {
    if (this.mediaElement.paused) {
      this.play();
    } else {
      this.pause();
    }
  }

  play() {
    this.mediaElement.play();
  }

  pause() {
    this.mediaElement.pause();
  }

  playStarted() {
    this.playButtonTargets.forEach(el => el.classList.add('hidden'));
    this.pauseButtonTargets.forEach(el => el.classList.remove('hidden'));
  }

  paused() {
    this.playButtonTargets.forEach(el => el.classList.remove('hidden'));
    this.pauseButtonTargets.forEach(el => el.classList.add('hidden'));
  }

  timeUpdate() {
    if (this.isScrubbing) {
      return false;
    }

    this.time = this.mediaElement.currentTime;

    this.setScrubPosition();
    this.logTime();
  }

  logTime() {
    const episodeId = this.mediaElement.dataset.episodeId;
    window.localStorage.setItem(`media-player-episode-${episodeId}`, JSON.stringify({ time: this.time }));
  }

  formatTime(time) {
    const minutes = Math.trunc(time / 60);
    const seconds = Math.trunc(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  jumpBack() {
    this.mediaElement.currentTime -= 10;
  }

  jumpForward() {
    this.mediaElement.currentTime += 10;
  }

  toggleMute() {
    this.mediaElement.muted = !this.mediaElement.muted;
    this.volumeTarget.classList.toggle('hidden');
    this.volumeMutedTarget.classList.toggle('hidden');
  }

  togglePlaybackRate() {
    if (this.mediaElement.playbackRate === 1) {
      this.mediaElement.playbackRate = 1.5;
    } else if (this.mediaElement.playbackRate === 1.5) {
      this.mediaElement.playbackRate = 2
    } else {
      this.mediaElement.playbackRate = 1;
    }
    this.setPlaybackRateVisibility();
  }

  setPlaybackRateVisibility() {
    this.playbackRateIconTargets.forEach(el => {
      if (el.dataset.rate === this.mediaElement.playbackRate.toString()) {
        el.classList.remove('hidden');
      } else {
        el.classList.add('hidden');
      }
    });
  }

  setScrubPosition() {
    const percent = this.time / this.mediaElement.duration;
    this.scrubberHandleTarget.style.left = `${percent * 100}%`;
    this.scrubberDoneTarget.style.width = `${percent * 100}%`;
    this.scrubberRangeTarget.value = Math.trunc(this.time);
    this.currentTimeTarget.innerText = this.formatTime(this.time);
    this.durationTarget.innerText = this.formatTime(this.mediaElement.duration);
  }
}

// TODO: Not sure why static targets in the class definition doesn't work - webpack loading error.
MediaPlayer.targets = [
  'playButton', // A button that displays when the  is paused
  'pauseButton', // A button that displays when the media is playing
  'scrubberDone', // A div that represents the darkened area already played
  'scrubberHandle', // A div that represents the 'end' of the scrubber
  'scrubber', // An element that represents the entire scrubber
  'scrubberRange', // An input range that represents the entire scrubber
  'currentTime', // A text indicator in MM:SS of the current position of the media
  'duration', // A text indicator in MM:SS of the total duration of the media
  'title', // The title of the episode
  "mediaElement", // The video media element (we use a global one for audio),
  "playbackRateIcon", // An icon for each playback rate,
  "volume", // An icon for volume (when not muted)
  "volumeMuted", // An icon for volume (when muted)
  "loadingButton", // A button that displays when the media is loading
];